<!-- Main modal -->
<div
  class="flex top-0 left-0 right-0 z-50 items-center justify-center w-full h-auto overflow-hidden md:inset-0 outline-none">
  <div class="relative w-screen max-w-5xl p-4 h-full">
    <div class="relative bg-white rounded-lg shadow h-full">
      <!-- Modal header -->
      <div
        class="p-4 sm:p-5 flex items-center justify-between pb-4 border-b border-gray-200 rounded-t">
        <div>
          <h3 class="text-xl font-bold text-gray-900">Book Appointment</h3>
        </div>

        <button
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 inline-flex cursor-pointer"
          data-modal-toggle="readEventModal"
          (click)="discard()">
          <svg
            aria-hidden="true"
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"></path>
          </svg>
          <span class="sr-only">Close modal</span>
        </button>
      </div>

      <!-- Main content -->
      <div
        class="p-4 sm:p-5 md:mb-5 sm:mb-4 overflow-auto max-h-[60vh] @container/appointment-dialog">
        @for (error of errorMessages(); track $index) {
          <p class="text-sm text-red-600 mb-2">{{ error }}</p>
        }
        <form
          [formGroup]="appointmentForm"
          class="flex gap-x-6 w-full overflow-x-hidden p-px flex-col @xl/appointment-dialog:flex-row">
          @if (initialLoading()) {
            <div class="flex justify-center items-center w-full h-[40vh]">
              <icaredx-spinner size="large"></icaredx-spinner>
            </div>
          } @else {
            <div class="space-y-6 w-sm flex-1 shrink-0 flex flex-col">
              <div>
                <label
                  for="language"
                  class="inline-block mb-2 text-sm text-gray-900 font-medium"
                  >Appointment language</label
                >
                <icaredx-dropdown-select
                  formControlName="language"
                  icaredxInputValidationMessage>
                  @for (language of providerLanguages; track language) {
                    <icaredx-option [value]="language.languageCode">
                      @if (language) {
                        <div class="flex items-center gap-x-2">
                          <img
                            class="inline-block w-4 h-4 object-cover"
                            [src]="language.languageFlagUrl"
                            [alt]="language.languageCode" />
                          <span class="text-sm text-gray-900">{{
                            language.languageName
                          }}</span>
                        </div>
                      } @else {
                        <span>{{ language }}</span>
                      }
                    </icaredx-option>
                  }
                </icaredx-dropdown-select>
              </div>
              @if (isProviderDialog) {
                <div>
                  <label
                    for="patient"
                    class="inline-block mb-2 text-sm text-gray-900 font-medium">
                    Select patient
                  </label>
                  <input
                    type="text"
                    data-testid="book-appointment-patient-select"
                    placeholder="Select Patient"
                    formControlName="patientId"
                    [readOnly]="hasPrefilledPatientId"
                    icaredxInputValidationMessage
                    [matAutocomplete]="autoPatient"
                    class="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5" />

                  <mat-autocomplete
                    class="bg-white! rounded-lg!"
                    #autoPatient="matAutocomplete"
                    [displayWith]="displayPatientFn.bind(this)"
                    (optionSelected)="(true)">
                    @for (patient of filteredPatients; track patient.id) {
                      <mat-option [value]="patient.id">{{
                        displayPatientFn(patient.id)
                      }}</mat-option>
                    }
                  </mat-autocomplete>
                </div>
              } @else {
                <div
                  class="inline-block mb-2 text-sm text-gray-900 font-medium">
                  Appointment with
                </div>
                <p
                  class="w-full bg-gray-50 border border-gray-300 text-gray-500 text-sm rounded-lg font-normal p-2.5">
                  {{ activeProvider()?.providerName }}
                </p>
              }
              <div>
                <label
                  for="appointment_type"
                  class="inline-block mb-2 text-sm text-gray-900 font-medium">
                  Choose Appointment Type
                </label>
                <icaredx-dropdown-select
                  formControlName="appointmentType"
                  icaredxInputValidationMessage>
                  @for (
                    appointmentType of appointmentTypes;
                    track appointmentType.id + '/' + appointmentType.mode
                  ) {
                    <icaredx-option
                      [value]="appointmentType.id + '/' + appointmentType.mode">
                      <span class="text-gray-900 text-sm">
                        {{
                          displayAppointmentTypeFn(
                            appointmentType.id + '/' + appointmentType.mode
                          )
                        }}
                      </span>
                    </icaredx-option>
                  }
                </icaredx-dropdown-select>
                @if (appointmentTypeControl.value) {
                  <div
                    class="flex mt-2 items-center gap-x-2 text-sm text-gray-600 font-medium">
                    <span class="py-0.5 px-2 bg-gray-200 rounded-md">
                      Cost:
                      {{
                        selectedAppointmentType?.cost
                          ? selectedAppointmentType!.cost + '$'
                          : 'Free'
                      }}
                    </span>
                    <span class="py-0.5 px-2 bg-gray-200 rounded-md"
                      >Duration:
                      {{ selectedAppointmentType?.durationMinutes }}
                      min
                    </span>
                  </div>
                }
                @if (noActiveTypes()) {
                  @if (isProviderDialog) {
                    <p
                      data-testid="book-appointment-no-types"
                      class="mt-4 text-xs text-gray-500"
                      i18n="@@booking-appointment.no-active-types">
                      No active appointment types available.
                    </p>
                  } @else {
                    <p
                      data-testid="book-appointment-no-types"
                      class="mt-4 text-xs text-gray-500"
                      i18n="@@booking-appointment.no-types-for-patient">
                      No appointment types are currently available for booking.
                    </p>
                  }
                }
                @if (appointmentTypesManagementUrl; as managementUrl) {
                  <div
                    class="text-xs text-gray-500"
                    [class.mt-4]="!noActiveTypes()">
                    @if (!noActiveTypes()) {
                      <p i18n="@@booking-appointment.only-active-types">
                        Only active appointment types are shown.
                      </p>
                    }
                    <p i18n="@@booking-appointment.manage-appointment-types">
                      Manage availability and appointment types in
                      <a
                        [href]="managementUrl"
                        class="rounded-sm text-primary-700 underline hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-700"
                        (click)="openAppointmentTypesManagement($event)"
                        >Appointment Types</a
                      >.
                    </p>
                  </div>
                }
              </div>
              @if (isProviderDialog) {
                <icaredx-appointment-note-field formControlName="note" />
              }
            </div>
            <div
              class="flex-1 @3xl/appointment-dialog:flex-2 flex gap-6 flex-col @3xl/appointment-dialog:flex-row">
              <div class="space-y-4 flex-1 min-w-2xs">
                <h3 class="text-gray-900 font-medium">Choose a date</h3>
                <icaredx-datepicker
                  formControlName="date"
                  [minDate]="minDate"
                  [maxDate]="maxDate()"
                  [availableDates]="enabledDates"
                  (dateRangeSelected)="onDateRangeSelected($event)">
                </icaredx-datepicker>
              </div>
              <div class="flex-1">
                <div class="space-y-4">
                  <div class="flex items-center justify-center gap-2">
                    <h3 class="text-gray-900 font-medium">
                      Available time slots
                    </h3>
                    <div class="relative inline-block w-30">
                      <input
                        class="bg-primary-50 border-0 text-primary-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full py-1 pl-7.5 pr-6"
                        type="text"
                        matInput
                        formControlName="timezoneId"
                        [matAutocomplete]="auto" />
                      <mat-autocomplete
                        [displayWith]="displayTimezoneFn"
                        panelWidth="auto"
                        #auto="matAutocomplete"
                        class="bg-white! rounded-lg! text-gray-900!"
                        (optionSelected)="
                          onTimezoneSelected($event.option.value)
                        ">
                        @for (zone of filteredTimezones; track zone) {
                          <mat-option [value]="zone.timeZoneId">{{
                            zone.fullName
                          }}</mat-option>
                        }
                      </mat-autocomplete>
                      <span
                        class="absolute left-2 top-1/2 transform -translate-y-1/2 text-blue-500">
                        <svg
                          class="w-4 h-4 text-primary-700"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </span>
                      <span
                        class="pointer-events-none absolute right-1.5 top-1/2 transform -translate-y-1/2 text-blue-500">
                        <svg
                          class="w-6 h-6 text-primary-700"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24">
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m8 10 4 4 4-4" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  @if (requestedStartMessage(); as message) {
                    <p
                      data-testid="book-appointment-requested-unavailable"
                      class="text-sm text-amber-700">
                      {{ message }}
                    </p>
                  }

                  @let timeslots = timeslots$ | async;
                  @if (loadingSlots()) {
                    <div class="flex justify-center items-center h-32">
                      <icaredx-spinner size="medium"></icaredx-spinner>
                    </div>
                  } @else if (noActiveTypes()) {
                    <div
                      class="text-gray-500"
                      i18n="@@booking-appointment.slots-need-active-type">
                      Available time slots will appear after an active
                      appointment type is selected.
                    </div>
                  } @else if (
                    !dateControl.value || !appointmentTypeControl.value
                  ) {
                    <div
                      class="text-gray-500"
                      i18n="@@booking-appointment.select-type-date">
                      Please select appointment type and date to see available
                      time slots.
                    </div>
                  } @else if (
                    !timeslots?.days?.[dateControl.value] ||
                    timeslots?.days?.[dateControl.value]?.length === 0
                  ) {
                    <div
                      class="text-gray-500"
                      i18n="@@booking-appointment.no-available-time-slots">
                      No available time slots for the selected date.
                    </div>
                  } @else {
                    <icaredx-radio-button-group formControlName="timeslot">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                        @for (
                          slot of timeslots?.days?.[dateControl.value ?? ''];
                          track slot
                        ) {
                          <icaredx-radio-timeslot
                            [value]="slot.startUtc"
                            [timezone]="timezoneIdControl?.value"
                            label="{{
                              slot.startUtc
                            }}"></icaredx-radio-timeslot>
                        }
                      </div>
                    </icaredx-radio-button-group>
                  }
                </div>
                <div></div>
              </div>
            </div>
          }
        </form>
      </div>

      <div
        class="p-4 sm:p-5 flex items-center justify-start border-t border-gray-200 gap-4">
        <icaredx-button
          data-testid="book-appointment-cancel"
          color="secondary"
          (clicked)="discard()"
          >Cancel</icaredx-button
        >

        <icaredx-button
          padding="px-3 py-2"
          [disabled]="
            noActiveTypes() || (!appointmentForm.valid && appointmentForm.dirty)
          "
          (clicked)="submit()">
          @if (submitting()) {
            <icaredx-spinner></icaredx-spinner>
          } @else {
            Confirm
          }
        </icaredx-button>
      </div>
    </div>
  </div>
</div>
